import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { localStorageVariables } from '@/types/localStorageVariables';
import { useAuth } from '@/lib/auth';
import { useApplicationLoading } from '@/contexts/ApplicationLoadingContext';
import { apiClientSecure } from '@/lib/api/index';
import { applyFavicon } from '@/lib/faviconUtils';

/**
 * Global navigation hook that mimics Angular's AppRightPanelComponent behavior.
 * Listens to route changes and automatically fetches right panel data to keep
 * localStorage updated with latest unit information for the menu display.
 * 
 * This ensures that regardless of which page the user lands on after login,
 * the left menu will show the correct unit data (bed count, bath count, area, etc.)
 */
export const useGlobalNavigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isLoading } = useAuth();
    const { isApplicationLoading, hasReceivedServerResponse } = useApplicationLoading();
    const previousPathname = useRef<string>('');

    // Routes where right panel data should not be fetched (matching Angular logic)
    const excludedRoutes = ['summary', 'myaccounts'];

    const shouldFetchRightPanelData = (pathname: string): boolean => {
        const pathSegments = pathname.split('/');
        const lastSegment = pathSegments[pathSegments.length - 1];
        
        // Don't fetch on excluded routes
        if (excludedRoutes.includes(lastSegment)) {
            return false;
        }
        
        // Don't fetch on login/access pages
        if (pathname.includes('/access/') || pathname.includes('/login')) {
            return false;
        }
        
        return true;
    };

    const fetchRightPanelData = async (): Promise<void> => {
        try {
            console.log('🏠 Global Navigation: Fetching right panel data...');
            
            const response = await apiClientSecure.get('home/getrightpanel');
            const rightPanelData = response.data;
            
            console.log('🏠 Global Navigation: Right panel data received:', rightPanelData);
            
            // Check for session errors
            if (rightPanelData.errorMessage === 'Invalid session') {
                console.warn('🏠 Global Navigation: Invalid session during right panel fetch');
                return; // Don't throw error, just skip the data update
            }
            
            // Update localStorage with unit details if available - matches Angular implementation
            if (rightPanelData.unitcode && rightPanelData.unitcode !== '') {
                localStorage.setItem(localStorageVariables.SelectedUnitCode, rightPanelData.unitcode);
            }
            
            if (rightPanelData.roomcode && rightPanelData.roomcode !== '') {
                localStorage.setItem(localStorageVariables.SelectedRoomCode, rightPanelData.roomcode);
            }
            
            if (rightPanelData.area && rightPanelData.area > 0) {
                localStorage.setItem(localStorageVariables.SelectedArea, rightPanelData.area.toString());
            }
            
            if (rightPanelData.areadisplayunit && rightPanelData.areadisplayunit !== '') {
                localStorage.setItem(localStorageVariables.SelectedAreaDisplay, rightPanelData.areadisplayunit);
            }
            
            if (rightPanelData.moveindate && rightPanelData.moveindate !== '') {
                // Format date similar to Angular TechUtils.FormatDateToString
                const moveInDate = formatDateToString(rightPanelData.moveindate);
                localStorage.setItem(localStorageVariables.SelectedMoveInDate, moveInDate);
            }
            
            if (rightPanelData.unitid && rightPanelData.unitid > 0) {
                localStorage.setItem(localStorageVariables.SelectedUnitID, rightPanelData.unitid.toString());
            }
            
            if (rightPanelData.floorPlanID && rightPanelData.floorPlanID > 0) {
                localStorage.setItem(localStorageVariables.FloorPlanID, rightPanelData.floorPlanID.toString());
            }
            
            if (rightPanelData.floorPlanName && rightPanelData.floorPlanName !== '') {
                localStorage.setItem(localStorageVariables.FloorPlanName, rightPanelData.floorPlanName);
            }
            
            // Update property information if available
            if (rightPanelData.propertyname && rightPanelData.propertyname !== '') {
                localStorage.setItem('PropertyName', rightPanelData.propertyname);
            }

                 
            // Update property information if available
            if (rightPanelData.Stu2GradeName && rightPanelData.Stu2GradeName !== '') {
                localStorage.setItem(localStorageVariables.SelGradeName, rightPanelData.Stu2GradeName);
            }
            
            console.log('🏠 Global Navigation: Updated localStorage with unit details:', {
                unitCode: rightPanelData.unitcode,
                bedCount: rightPanelData.bedcount,
                bathCount: rightPanelData.bathroomcount,
                area: rightPanelData.area,
                areaDisplayUnit: rightPanelData.areadisplayunit,
                floorPlanName: rightPanelData.floorPlanName
            });
            
            // Dispatch custom event to notify components of localStorage update
            window.dispatchEvent(new CustomEvent('localStorageUpdated', {
                detail: { source: 'globalNavigation' }
            }));
            window.dispatchEvent(new Event('unitDataUpdated'));
            
        } catch (error) {
            console.warn('🏠 Global Navigation: Error fetching right panel data (non-critical):', error);
            // Don't throw error - this is non-critical and shouldn't break navigation
        }
    };

    const formatDateToString = (dateString: string): string => {
        // Format date for storage - implement according to your needs
        try {
            if (!dateString) return '';
            return new Date(dateString).toISOString();
        } catch (error) {
            console.warn('Invalid date string provided:', dateString, error);
            return '';
        }
    };

    // Listen to route changes and fetch right panel data when needed
    useEffect(() => {
        const currentPathname = location.pathname;
        checkPageTitle(currentPathname);
        // Only fetch if the pathname actually changed (not just hash or search params)
        if (previousPathname.current !== currentPathname) {
            console.log('🏠 Global Navigation: Route changed from', previousPathname.current, 'to', currentPathname);
            
            if (shouldFetchRightPanelData(currentPathname)) {
                console.log('🏠 Global Navigation: Fetching right panel data for route:', currentPathname);
                
                // Small delay to ensure the page has settled after navigation
                const timeoutId = setTimeout(() => {
                    fetchRightPanelData();
                }, 100);
                
                // Store cleanup function
                return () => clearTimeout(timeoutId);
            } else {
                console.log('🏠 Global Navigation: Skipping right panel data fetch for route:', currentPathname);
            }
            
            previousPathname.current = currentPathname;
        }
    }, [location.pathname, navigate, user, isLoading]);

    // Return nothing - this is a side-effect only hook
    return null;
};

export const checkPageTitle = (currentPathname: string) => {
    try {
        const propertyName = localStorage.getItem(localStorageVariables.PropertyName) || '';
        const propertyLogoUrl = localStorage.getItem(localStorageVariables.PropertyLogoUrl) || '';

        // Set page title based on the current route
        const pathSegments = currentPathname.split('/').filter(Boolean);
        const lastSegment = pathSegments[pathSegments.length - 1]?.toLowerCase();

        if (lastSegment === 'login' || lastSegment === 'guestlogin') {
            document.title = `Login to ${propertyName} to track your account | ${propertyName}`;
        } else if (lastSegment === 'registration') {
            document.title = `Register with ${propertyName} to track your account | ${propertyName}`;
        } else if (propertyName) {
            document.title = propertyName;
        }

        // Set favicon to property logo URL if available (first load, refresh, and on navigation)
        if (propertyLogoUrl) {
            applyFavicon(propertyLogoUrl);
        }
    } catch (error) {
        console.error('Error checking page title:', error);
    }
}